import { CrudFilters } from "@pankod/refine-core";
import dayjs, { Dayjs } from "dayjs";

import { ILeads } from "interfaces";
import {
  budgetStatusMap,
  budgetWppInegibleReasonMap,
} from "utils/constants";
import { formatCPF } from "utils/formaters";

export const PAGE_SIZE = 100;
export const FILTER_EQUALS_OPERATOR = "eq";

type StateOption = {
  id: string;
  value: string;
  name: string;
  label: string;
};

export const STATES_LIST_BRAZIL: StateOption[] = [
  { id: "AC", value: "AC", name: "Acre", label: "AC" },
  { id: "AL", value: "AL", name: "Alagoas", label: "AL" },
  { id: "AP", value: "AP", name: "Amapá", label: "AP" },
  { id: "AM", value: "AM", name: "Amazonas", label: "AM" },
  { id: "BA", value: "BA", name: "Bahia", label: "BA" },
  { id: "CE", value: "CE", name: "Ceará", label: "CE" },
  { id: "DF", value: "DF", name: "Distrito Federal", label: "DF" },
  { id: "ES", value: "ES", name: "Espírito Santo", label: "ES" },
  { id: "GO", value: "GO", name: "Goiás", label: "GO" },
  { id: "MA", value: "MA", name: "Maranhão", label: "MA" },
  { id: "MT", value: "MT", name: "Mato Grosso", label: "MT" },
  { id: "MS", value: "MS", name: "Mato Grosso do Sul", label: "MS" },
  { id: "MG", value: "MG", name: "Minas Gerais", label: "MG" },
  { id: "PA", value: "PA", name: "Pará", label: "PA" },
  { id: "PB", value: "PB", name: "Paraíba", label: "PB" },
  { id: "PR", value: "PR", name: "Paraná", label: "PR" },
  { id: "PE", value: "PE", name: "Pernambuco", label: "PE" },
  { id: "PI", value: "PI", name: "Piauí", label: "PI" },
  { id: "RJ", value: "RJ", name: "Rio de Janeiro", label: "RJ" },
  { id: "RN", value: "RN", name: "Rio Grande do Norte", label: "RN" },
  { id: "RS", value: "RS", name: "Rio Grande do Sul", label: "RS" },
  { id: "RO", value: "RO", name: "Rondônia", label: "RO" },
  { id: "RR", value: "RR", name: "Roraima", label: "RR" },
  { id: "SC", value: "SC", name: "Santa Catarina", label: "SC" },
  { id: "SP", value: "SP", name: "São Paulo", label: "SP" },
  { id: "SE", value: "SE", name: "Sergipe", label: "SE" },
  { id: "TO", value: "TO", name: "Tocantins", label: "TO" },
];

export type BudgetSearchFormValues = {
  budget_id?: string;
  cpf?: string;
  city?: string;
  state?: string;
  status?: string | number;
  plan_base_code?: string | number;
  ctrl_number?: string;
  cell_phone?: string;
  created_start?: Dayjs;
  created_end?: Dayjs;
  updated_start?: Dayjs;
  updated_end?: Dayjs;
  payment_start?: Dayjs;
  payment_end?: Dayjs;
  bill_emission_start?: Dayjs;
  bill_emission_end?: Dayjs;
  health_insurer?: string | number;
  last_screen?: string;
  partner?: string;
  branch_code?: string;
  friendly_plan_name?: string;
  ans_code?: string;
  budget_number?: string;
};

export type BudgetSearchParams = {
  budget_id?: string;
  cpf?: string;
  city?: string;
  state?: string;
  status?: string | number;
  plan_base_code?: string | number;
  ctrl_number?: string;
  cell_phone?: string;
  created_start?: string;
  created_end?: string;
  updated_start?: string;
  updated_end?: string;
  payment_start?: string;
  payment_end?: string;
  bill_emission_start?: string;
  bill_emission_end?: string;
  health_insurer?: string | number;
  last_screen?: string;
  partner?: string;
  branch_code?: string;
  friendly_plan_name?: string;
  ans_code?: string;
  budget_number?: string;
};

const formatDateFilter = (
  date: Dayjs | string | null | undefined,
  isStart: boolean,
): string | null => {
  if (!date) {
    return null;
  }

  if (isStart) {
    return dayjs(date).format("YYYY-MM-DD 00:00:00");
  }
  return dayjs(date).format("YYYY-MM-DD 23:59:59");
};

const formatDateParam = (
  date: Dayjs | string | null | undefined,
  isStart: boolean,
): string => {
  if (!date) {
    return "";
  }

  if (isStart) {
    return dayjs(date).format("YYYY-MM-DD 00:00:00");
  }
  return dayjs(date).format("YYYY-MM-DD 23:59:59");
};

export const buildSearchFilters = (
  values: BudgetSearchFormValues,
): CrudFilters => {
  const filterFields = [
    "_id",
    "cpf",
    "city",
    "state",
    "status",
    "partner",
    "ctrl_number",
    "cell_phone",
    "health_insurer",
    "payment_start",
    "payment_end",
    "created_start",
    "created_end",
    "branch_code",
    "plan_base_code",
    "friendly_plan_name",
    "ans_code",
    "budget_number",
    "updated_start",
    "updated_end",
    "bill_emission_start",
    "bill_emission_end",
    "last_screen",
  ] as const;

  type FilterField = typeof filterFields[number];
  type FilterValue = string | number | null;

  const filterValues: Record<FilterField, FilterValue> = {
    _id: values?.budget_id ?? null,
    cpf: values?.cpf ?? null,
    city: values?.city ?? null,
    state: values?.state ?? null,
    status: values?.status ?? null,
    partner: values?.partner ?? null,
    ctrl_number: values?.ctrl_number ?? null,
    cell_phone: values?.cell_phone ?? null,
    health_insurer: values?.health_insurer ?? null,
    payment_start: values?.payment_start
      ? formatDateFilter(values.payment_start, true)
      : null,
    payment_end: values?.payment_end
      ? formatDateFilter(values.payment_end, false)
      : null,
    created_start: values?.created_start
      ? formatDateFilter(values.created_start, true)
      : null,
    created_end: values?.created_end
      ? formatDateFilter(values.created_end, false)
      : null,
    branch_code: values?.branch_code ?? null,
    plan_base_code: values?.plan_base_code ?? null,
    friendly_plan_name: values?.friendly_plan_name ?? null,
    ans_code: values?.ans_code ?? null,
    budget_number: values?.budget_number ?? null,
    updated_start: values?.updated_start
      ? formatDateFilter(values.updated_start, true)
      : null,
    updated_end: values?.updated_end
      ? formatDateFilter(values.updated_end, false)
      : null,
    bill_emission_start: values?.bill_emission_start
      ? formatDateFilter(values.bill_emission_start, true)
      : null,
    bill_emission_end: values?.bill_emission_end
      ? formatDateFilter(values.bill_emission_end, false)
      : null,
    last_screen: values?.last_screen ?? null,
  };

  return filterFields.map((field) => ({
    field,
    operator: FILTER_EQUALS_OPERATOR,
    value: filterValues[field],
  }));
};

const exportFilterFields = [
  "_id",
  "cpf",
  "city",
  "state",
  "status",
  "partner",
  "ctrl_number",
  "cell_phone",
  "health_insurer",
  "created_start",
  "created_end",
  "updated_start",
  "updated_end",
  "payment_start",
  "payment_end",
  "bill_emission_start",
  "bill_emission_end",
  "branch_code",
  "plan_base_code",
  "friendly_plan_name",
  "ans_code",
  "budget_number",
  "last_screen",
] as const;

type ExportFilterField = typeof exportFilterFields[number];
type ExportFilterValue = string | number | null | undefined;

export const buildExportFilters = (
  params?: BudgetSearchParams,
): CrudFilters => {
  const values: Record<ExportFilterField, ExportFilterValue> = {
    _id: params?.budget_id,
    cpf: params?.cpf,
    city: params?.city,
    state: params?.state,
    status: params?.status,
    partner: params?.partner,
    ctrl_number: params?.ctrl_number,
    cell_phone: params?.cell_phone,
    health_insurer: params?.health_insurer,
    created_start: params?.created_start,
    created_end: params?.created_end,
    updated_start: params?.updated_start,
    updated_end: params?.updated_end,
    payment_start: params?.payment_start,
    payment_end: params?.payment_end,
    bill_emission_start: params?.bill_emission_start,
    bill_emission_end: params?.bill_emission_end,
    branch_code: params?.branch_code,
    plan_base_code: params?.plan_base_code,
    friendly_plan_name: params?.friendly_plan_name,
    ans_code: params?.ans_code,
    budget_number: params?.budget_number,
    last_screen: params?.last_screen,
  };

  return exportFilterFields.map((field) => ({
    field,
    operator: FILTER_EQUALS_OPERATOR,
    value: values[field],
  }));
};

export const buildSearchParams = (
  values: BudgetSearchFormValues,
): BudgetSearchParams => {
  return {
    budget_id: values?.budget_id,
    cpf: values?.cpf,
    city: values?.city,
    state: values?.state,
    status: values?.status,
    plan_base_code: values?.plan_base_code,
    budget_number: values?.budget_number,
    ctrl_number: values?.ctrl_number,
    cell_phone: values?.cell_phone,
    created_start: formatDateParam(values?.created_start, true),
    created_end: formatDateParam(values?.created_end, false),
    payment_start: formatDateParam(values?.payment_start, true),
    payment_end: formatDateParam(values?.payment_end, false),
    updated_start: formatDateParam(values?.updated_start, true),
    updated_end: formatDateParam(values?.updated_end, false),
    bill_emission_start: formatDateParam(values?.bill_emission_start, true),
    bill_emission_end: formatDateParam(values?.bill_emission_end, false),
    health_insurer: values?.health_insurer,
    last_screen: values?.last_screen,
    partner: values?.partner,
    branch_code: values?.branch_code,
    friendly_plan_name: values?.friendly_plan_name,
    ans_code: values?.ans_code,
  };
};

const getWhatsappLabel = (data?: ILeads["WhatsApp"]): string => {
  if (!data) return "";
  const reasonConfig = budgetWppInegibleReasonMap[data];
  return reasonConfig?.label ?? "";
};

const normalizeBudgetStatus = (status: unknown): number | null => {
  if (typeof status === "number" && Number.isFinite(status)) {
    return status;
  }

  if (typeof status === "string" && status.trim() !== "") {
    const parsed = Number(status);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

export const getBudgetStatusLabel = (
  status: unknown,
  fallback = "",
): string => {
  const normalized = normalizeBudgetStatus(status);
  if (normalized === null) return fallback;

  if (Object.prototype.hasOwnProperty.call(budgetStatusMap, normalized)) {
    return budgetStatusMap[normalized as keyof typeof budgetStatusMap].label;
  }

  return fallback;
};

export const mapBudgetExportData = (item: ILeads) => ({
  codigo_orcamento: item?._id ?? "",
  data: item?.created_at ?? "",
  data_atualizacao: item?.updated_at ?? "",
  nome: item?.name ?? "",
  cpf: formatCPF(item?.cpf),
  cidade: item?.city ?? "",
  uf: item?.state ?? "",
  status_codigo: item?.status ?? "",
  status_descricao: getBudgetStatusLabel(item?.status, ""),
  telefone: item?.cell_phone ?? "",
  email: item?.email ?? "",
  operadora: item?.health_insurer ?? "",
  nu_controle: item?.control_number ?? "",
  produto: item?.product ?? "",
  num_vidas: item?.dependents ?? "",
  ultima_tela: item?.last_screen ?? "",
  partner: item?.partner ?? "",
  origem: item?.origin ?? "",
  data_pagamento: item?.payment_details?.PaymentDate ?? "",
  data_emissao_boleto: item?.payment_details?.BillEmissionDate ?? "",
  codigo_afiliado: item?.affiliate ?? "",
  whatsapp: getWhatsappLabel(item?.WhatsApp),
  entry_url: item?.EntryUrl ?? "",
  Gclid: item?.Gclid ?? "",
});

export const getValidFilters = (filters: CrudFilters): CrudFilters => {
  return filters.filter((filter) => filter.value !== "");
};
